import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { ChangeRequestView, CreateChangeRequestInput } from '@shared/schemas/change-request';
import type { ClientPropertyInput } from '@shared/schemas/client';
import type {
  ClientDashboard,
  ClientJobDetail,
  ClientJobListItem,
  ClientNotificationPreferences,
  ClientProfileView,
  ClientPropertyPortalView,
  NotificationPreferenceInput,
  RecoveryEmailInput,
} from '@shared/schemas/client-portal';
import type { JobStatusValue } from '@shared/schemas/job';
import type { ClientJobRequestView, CreateJobRequestInput } from '@shared/schemas/job-request';
import type { ListResponse } from '@shared/schemas/pagination';

/*
 * The client portal's queries. ROADMAP.md §5.
 *
 * These go through `@/lib/api`, which throws on any non-2xx — so a failed
 * request arrives as `error` and renders as an error, never as an empty list
 * (P22). Nothing in this portal reads `data ?? []` without also asking whether
 * the request succeeded.
 */

export const CLIENT_ROOT = ['client'] as const;

export function useClientDashboard() {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'dashboard'],
    queryFn: () => api.get<ClientDashboard>('/api/client/dashboard'),
  });
}

export interface ClientJobListParams {
  page: number;
  search: string;
  status: JobStatusValue | 'all';
}

export function useClientJobs(params: ClientJobListParams) {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'jobs', 'list', params],
    queryFn: () =>
      api.get<ListResponse<ClientJobListItem>>(
        `/api/client/jobs${queryString({
          page: params.page,
          search: params.search,
          status: params.status === 'all' ? undefined : params.status,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useClientJob(id: string | undefined) {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'jobs', 'detail', id],
    queryFn: () => api.get<ClientJobDetail>(`/api/client/jobs/${id ?? ''}`),
    enabled: id !== undefined,
  });
}

/*
 * Properties — ROADMAP.md §5.3.
 *
 * Every mutation invalidates the whole `client` key rather than one list. The
 * counts on the dashboard and the primary address in the profile summary are
 * derived from these rows, and a page that shows "1 property" beside two cards
 * is the kind of disagreement people notice and distrust.
 */

function invalidateAll(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: CLIENT_ROOT });
}

export function useClientProperties() {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'properties'],
    queryFn: () => api.get<{ data: ClientPropertyPortalView[] }>('/api/client/properties'),
  });
}

export function useSaveClientProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { propertyId: string | null; input: ClientPropertyInput }) =>
      params.propertyId === null
        ? api.post<ClientPropertyPortalView>('/api/client/properties', params.input)
        : api.patch<ClientPropertyPortalView>(
            `/api/client/properties/${params.propertyId}`,
            params.input,
          ),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export type ClientPropertyAction = 'primary' | 'retire' | 'reactivate';

export function useClientPropertyAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { propertyId: string; action: ClientPropertyAction }) =>
      api.post<unknown>(`/api/client/properties/${params.propertyId}/${params.action}`),
    onSuccess: () => invalidateAll(queryClient),
  });
}

/* Requests — ROADMAP.md §5.4. */

/** The services a client may ask for. `is_requestable`, not the whole rate card. */
export interface RequestableService {
  id: string;
  name: string;
  description: string | null;
  defaultDurationMinutes: number;
}

export function useRequestableServices() {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'services'],
    queryFn: () => api.get<{ data: RequestableService[] }>('/api/client/job-requests/services'),
    // The rate card changes about once a year. Refetching it on every visit to
    // the request form is a round trip for nothing.
    staleTime: 5 * 60 * 1000,
  });
}

export function useClientJobRequests(params: { page: number }) {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'job-requests', params],
    queryFn: () =>
      api.get<ListResponse<ClientJobRequestView>>(
        `/api/client/job-requests${queryString({ page: params.page })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateJobRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJobRequestInput) =>
      api.post<ClientJobRequestView>('/api/client/job-requests', input),
    onSuccess: () => invalidateAll(queryClient),
  });
}

/* Reschedule and cancellation requests — ROADMAP.md §5.6. */

export function useClientChangeRequests(params: { page: number; status: 'pending' | 'all' }) {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'change-requests', params],
    queryFn: () =>
      api.get<ListResponse<ChangeRequestView>>(
        `/api/client/change-requests${queryString({ page: params.page, status: params.status })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateChangeRequestInput) =>
      api.post<ChangeRequestView>('/api/client/change-requests', input),
    onSuccess: () => invalidateAll(queryClient),
  });
}

/* Profile and notification preferences — ROADMAP.md §5.7. */

export function useClientProfile() {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'profile'],
    queryFn: () => api.get<ClientProfileView>('/api/client/profile'),
  });
}

export function useSetRecoveryEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RecoveryEmailInput) =>
      api.put<ClientProfileView>('/api/client/profile/recovery-email', input),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: [...CLIENT_ROOT, 'notification-preferences'],
    queryFn: () =>
      api.get<ClientNotificationPreferences>('/api/client/profile/notification-preferences'),
  });
}

export function useSetNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: NotificationPreferenceInput) =>
      api.put<ClientNotificationPreferences>('/api/client/profile/notification-preferences', input),
    onSuccess: (data) => {
      // Written straight into the cache as well as invalidated: a toggle that
      // visibly springs back while a refetch lands reads as the save failing.
      queryClient.setQueryData([...CLIENT_ROOT, 'notification-preferences'], data);
      return invalidateAll(queryClient);
    },
  });
}
